import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { deriveWarnings } from "@/lib/deploymentWarnings";

export { deriveWarnings };  // re-export for backwards compat with existing imports

/* Admin-only deploy-config warning strip.
   Complements BackendHealthBanner (which fires when the BACKEND is unreachable
   from the frontend — a red critical-error state visible to every user). This
   banner is amber, admin-scoped, and surfaces the SILENT misconfigurations
   that don't crash the app but degrade specific flows:
     - Stripe disabled → no upgrades / no revenue
     - Stripe webhook secret missing → renewals/cancellations won't sync
     - Email (Resend) disabled → new signups can't verify, resets don't send
     - Bootstrap not complete or last_error set → indexes/migrations/seed skipped
     - Migrations pending
     - Admin seed missing
     - DB unreachable (belt-and-braces; also caught by the red banner)

   Runs a single fetch on mount once the current user is confirmed admin.
   Dismissal is session-scoped (per-page-load) — deliberate; we don't want
   ops to be able to bury a real deploy issue with one click forever. */

export function AdminConfigWarningBanner() {
  const { user, authReady } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [nonce, setNonce] = useState(0);

  const isAdmin = authReady && user?.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setLoading(true);
    api
      .get("/api/health/detailed")
      .then((r) => {
        if (!cancelled) setSnapshot(r.data);
      })
      .catch(() => {
        // Endpoint itself failed — leave snapshot null; BackendHealthBanner
        // handles the "backend unreachable" story. We don't double-surface.
        if (!cancelled) setSnapshot(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, nonce]);

  const warnings = useMemo(() => deriveWarnings(snapshot), [snapshot]);

  if (!isAdmin || dismissed || warnings.length === 0) return null;

  return (
    <div
      className="border-b border-amber-300 bg-amber-100 text-amber-900"
      data-testid="admin-config-warning-banner"
      role="alert"
    >
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" aria-hidden="true" />
        <div className="min-w-0 flex-1 text-sm">
          <div className="font-semibold">
            Deploy configuration warning{warnings.length > 1 ? "s" : ""}
            <span className="ml-1 text-amber-800/80 font-normal">
              ({warnings.length}) — visible to admins only
            </span>
          </div>
          <ul className="mt-1 space-y-1">
            {warnings.map((w) => (
              <li
                key={w.key}
                data-testid={`admin-config-warning-${w.key}`}
                className="flex flex-wrap items-baseline gap-x-2"
              >
                <span className="font-medium">{w.title}:</span>
                <span className="text-amber-900/90">{w.message}</span>
              </li>
            ))}
          </ul>
          <div className="mt-1.5 text-[13px] text-amber-900/80">
            Open{" "}
            <Link
              to="/admin/overview"
              className="underline font-medium hover:text-amber-950"
              data-testid="admin-config-warning-open-health"
            >
              Admin › System Health
            </Link>{" "}
            for full details, or fix the missing config on your deploy and redeploy.
          </div>
        </div>
        <div className="flex items-start gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setNonce((n) => n + 1)}
            disabled={loading}
            className="p-1.5 rounded hover:bg-amber-200 disabled:opacity-50"
            data-testid="admin-config-warning-refresh"
            aria-label="Recheck configuration"
            title="Recheck configuration"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="p-1.5 rounded hover:bg-amber-200"
            data-testid="admin-config-warning-dismiss"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminConfigWarningBanner;
